/**
 * PATH: packages/ui/src/kanban/components/KanbanCardHoverMenuStrip.tsx
 * FILE_ID: UI-KANBAN-CARD-HOVER-STRIP-001
 * PURPOSE: Icon buttons inside the card hover strip (Colour / Draft / Calc / Flow).
 * WHY: Keeps KanbanCardHoverMenu under the 500-line ceiling; strip chrome stays
 *   in the frame while button contracts live here.
 * FLOW: capability flags → buttons → openPanel / open workbook / flowchart.
 * RELATED: KanbanCardHoverMenu.tsx.
 */
import { Calculator, Palette, PenSquare, Workflow } from 'lucide-react'
import type { Card } from '../types'
import type { PanelKind } from './KanbanCardHoverMenu.types'

const ICON_BTN =
  'inline-flex h-7 w-7 items-center justify-center rounded-xl text-neutral-500 transition-[background-color,color,transform] duration-150 hover:bg-neutral-100 hover:text-neutral-900 active:scale-90 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-neutral-400 disabled:active:scale-100 dark:text-zinc-400 dark:hover:bg-white/10 dark:hover:text-zinc-50'

const ICON_BTN_ACTIVE =
  'bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 dark:bg-blue-500/20 dark:text-blue-300 dark:hover:bg-blue-500/25 dark:hover:text-blue-200'

const ICON_GLYPH = 'h-[17px] w-[17px]'

const STRIP_DIVIDER = 'my-0.5 h-px w-4 rounded-full bg-neutral-200/80 dark:bg-white/10'

export interface KanbanCardHoverMenuStripProps {
  card: Card
  panel: PanelKind | null
  hasColor: boolean
  hasDraft: boolean
  hasWorkbook: boolean
  hasFlowchart: boolean
  armPanelOnHover: (kind: PanelKind, e: React.MouseEvent) => void
  cancelHoverOpen: () => void
  openPanel: (kind: PanelKind, e: React.MouseEvent) => void
  onOpenWorkbook?: (card: Card) => void
  onPrefetchWorkbook?: (card: Card) => void
  onOpenFlowchart?: (card: Card) => void
  setPanel: (panel: PanelKind | null) => void
}

export function KanbanCardHoverMenuStrip({
  card,
  panel,
  hasColor,
  hasDraft,
  hasWorkbook,
  hasFlowchart,
  armPanelOnHover,
  cancelHoverOpen,
  openPanel,
  onOpenWorkbook,
  onPrefetchWorkbook,
  onOpenFlowchart,
  setPanel,
}: KanbanCardHoverMenuStripProps) {
  return (
    <>
      {hasColor && (
        <button
          type="button"
          draggable={false}
          data-card-action="color"
          data-testid="kanban-card-color-trigger"
          aria-label="Colour and labels"
          aria-expanded={panel === 'color'}
          title="Colour and labels"
          onMouseEnter={(e) => armPanelOnHover('color', e)}
          onMouseLeave={cancelHoverOpen}
          onClick={(e) => openPanel('color', e)}
          className={[ICON_BTN, panel === 'color' ? ICON_BTN_ACTIVE : ''].join(' ')}
        >
          <Palette className={ICON_GLYPH} aria-hidden="true" />
        </button>
      )}
      {hasDraft && (
        <button
          type="button"
          draggable={false}
          data-card-action="draft"
          data-testid="kanban-card-draft-trigger"
          aria-label="Draft email from card"
          aria-expanded={panel === 'draft'}
          title="Draft email from card"
          onMouseEnter={(e) => armPanelOnHover('draft', e)}
          onMouseLeave={cancelHoverOpen}
          onClick={(e) => openPanel('draft', e)}
          className={[ICON_BTN, panel === 'draft' ? ICON_BTN_ACTIVE : ''].join(' ')}
        >
          <PenSquare className={ICON_GLYPH} aria-hidden="true" />
        </button>
      )}
      {(hasColor || hasDraft) && <span aria-hidden="true" className={STRIP_DIVIDER} />}
      {/* FEAT-2026-06-30 — Workbook / Flowchart. Prefetch on Calculator hover only. */}
      <button
        type="button"
        disabled={!hasWorkbook}
        data-card-action="calculator"
        data-testid="kanban-card-calculator-trigger"
        aria-label={hasWorkbook ? 'Open workbook' : 'Workbook (coming soon)'}
        title={hasWorkbook ? 'Workbook' : 'Workbook (coming soon)'}
        onMouseEnter={hasWorkbook ? () => onPrefetchWorkbook?.(card) : undefined}
        onFocus={hasWorkbook ? () => onPrefetchWorkbook?.(card) : undefined}
        onClick={
          hasWorkbook
            ? (e) => {
                e.stopPropagation()
                e.preventDefault()
                setPanel(null)
                onPrefetchWorkbook?.(card)
                onOpenWorkbook?.(card)
              }
            : undefined
        }
        className={ICON_BTN}
      >
        <Calculator className={ICON_GLYPH} aria-hidden="true" />
      </button>
      <button
        type="button"
        disabled={!hasFlowchart}
        data-card-action="flowchart"
        data-testid="kanban-card-flowchart-trigger"
        aria-label={hasFlowchart ? 'Open flowchart' : 'Flowchart (coming soon)'}
        title={hasFlowchart ? 'Flowchart' : 'Flowchart (coming soon)'}
        onClick={
          hasFlowchart
            ? (e) => {
                e.stopPropagation()
                e.preventDefault()
                setPanel(null)
                onOpenFlowchart?.(card)
              }
            : undefined
        }
        className={ICON_BTN}
      >
        <Workflow className={ICON_GLYPH} aria-hidden="true" />
      </button>
    </>
  )
}
